/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * One quiet line saying which clock a page's times are on, shown only while this
 * device's clock reads differently from the gym's.
 */
import React from "react";
import { Link } from "react-router-dom";
import { IconChevronRight } from "@tabler/icons-react";
import type { TimeZoneDisplayPreference } from "@lemonadetraining/contracts";
import { cn } from "@lemonadetraining/web-ui";

export { gymTimeLabel } from "./gymTimeLabel";

export const TIME_SETTINGS_PATH = "/dashboard/profile?tab=appearance#show-times-in";

interface ClockNoteProps {
  variant: "link" | "static";
  mode: TimeZoneDisplayPreference;
  city: string | null;
  differs: boolean;
  className?: string;
}

const gymTimeNote = (city: string | null) =>
  city ? (
    <>
      Gym time
      <span aria-hidden="true"> · </span>
      <span className="sr-only">, </span>
      {city}
    </>
  ) : (
    "Gym time"
  );

const ClockNote: React.FC<ClockNoteProps> = ({ variant, mode, city, differs, className }) => {
  if (!differs) return null;

  if (variant === "static") {
    return (
      <p className={cn("text-sm text-muted-foreground", className)}>
        {gymTimeNote(city)}
        <span className="sr-only">
          . Times on this page are your gym&apos;s time. This device is on a different time zone.
        </span>
      </p>
    );
  }

  return (
    <Link
      to={TIME_SETTINGS_PATH}
      className={cn(
        "inline-flex min-h-11 items-center gap-1 rounded-sm text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        className
      )}
    >
      {mode === "gym" ? (
        <span>
          {gymTimeNote(city)}
          <span className="sr-only">
            . Times are shown in your gym&apos;s time. This device is on a different time zone. Open
            time settings.
          </span>
        </span>
      ) : (
        <span>
          Your device&apos;s time, not {city ?? "gym"} time
          <span className="sr-only">
            . Times are shown in this device&apos;s time. Open time settings.
          </span>
        </span>
      )}
      <IconChevronRight className="size-3.5" aria-hidden="true" />
    </Link>
  );
};

export default ClockNote;
